import React from 'react';
import { Upload, Trash2 } from 'lucide-react';
import type { CountdownTimerData } from './types';
import { OverlaySettings } from './OverlaySettings';

interface BackgroundSectionProps {
  data: CountdownTimerData;
  setFormData: (data: CountdownTimerData) => void;
  onOpenMediaUpload: (mediaType: 'image' | 'video', assetType: string) => void;
}

export const BackgroundSection: React.FC<BackgroundSectionProps> = ({
  data,
  setFormData,
  onOpenMediaUpload,
}) => {
  const handleEnableBackground = (enabled: boolean) => {
    setFormData({
      ...data,
      backgroundImage: {
        ...(data.backgroundImage || {}),
        enabled,
        url: data.backgroundImage?.url || '',
        cloudfrontUrl: data.backgroundImage?.cloudfrontUrl || '',
        position: data.backgroundImage?.position || 'center',
        size: data.backgroundImage?.size || 'cover',
        repeat: data.backgroundImage?.repeat || 'no-repeat',
        parallax: data.backgroundImage?.parallax || false,
        overlay: data.backgroundImage?.overlay || {
          enabled: false,
          color: '#000000',
          opacity: 0.5,
        },
        alt: data.backgroundImage?.alt || '',
      },
    });
  };

  return (
    <div className="border-b pb-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Background</h3>

      <div className="space-y-4">
        <div>
          <label className="flex items-center space-x-2 mb-2">
            <input
              type="checkbox"
              checked={data.backgroundImage?.enabled || false}
              onChange={e => handleEnableBackground(e.target.checked)}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded-md focus:ring-primary-500"
            />
            <span className="text-sm font-medium text-gray-700">Enable Background Image</span>
          </label>
        </div>

        {data.backgroundImage?.enabled && (
          <div className="space-y-3 pl-4 border-l-2 border-gray-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Image URL *</label>
              <div className="flex space-x-2">
                <input
                  type="url"
                  value={data.backgroundImage?.url || data.backgroundImage?.cloudfrontUrl || ''}
                  onChange={e =>
                    setFormData({
                      ...data,
                      backgroundImage: {
                        ...data.backgroundImage!,
                        url: e.target.value,
                        cloudfrontUrl: '',
                      },
                    })
                  }
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="https://example.com/image.jpg"
                />
                <button
                  type="button"
                  onClick={() => onOpenMediaUpload('image', 'background')}
                  className="px-3 py-2 text-sm text-white bg-primary-600 hover:bg-primary-700 rounded-md flex items-center space-x-1"
                >
                  <Upload size={16} />
                  <span>Upload</span>
                </button>
                {(data.backgroundImage?.url || data.backgroundImage?.cloudfrontUrl) && (
                  <button
                    type="button"
                    onClick={() =>
                      setFormData({
                        ...data,
                        backgroundImage: { ...data.backgroundImage!, url: '', cloudfrontUrl: '' },
                      })
                    }
                    className="px-3 py-2 text-sm text-red-600 hover:text-red-700 border border-red-300 hover:border-red-400 rounded-md flex items-center space-x-1"
                    title="Remove image"
                  >
                    <Trash2 size={16} />
                    <span>Remove</span>
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Position</label>
                <select
                  value={data.backgroundImage?.position || 'center'}
                  onChange={e =>
                    setFormData({
                      ...data,
                      backgroundImage: { ...data.backgroundImage!, position: e.target.value },
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="center">Center</option>
                  <option value="top">Top</option>
                  <option value="bottom">Bottom</option>
                  <option value="left">Left</option>
                  <option value="right">Right</option>
                  <option value="top-left">Top Left</option>
                  <option value="top-right">Top Right</option>
                  <option value="bottom-left">Bottom Left</option>
                  <option value="bottom-right">Bottom Right</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Size</label>
                <select
                  value={data.backgroundImage?.size || 'cover'}
                  onChange={e =>
                    setFormData({
                      ...data,
                      backgroundImage: { ...data.backgroundImage!, size: e.target.value },
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="cover">Cover</option>
                  <option value="contain">Contain</option>
                  <option value="auto">Auto</option>
                  <option value="100%">100%</option>
                </select>
              </div>
            </div>

            <div>
              <label className="flex items-center space-x-2 mb-2">
                <input
                  type="checkbox"
                  checked={data.backgroundImage?.parallax || false}
                  onChange={e =>
                    setFormData({
                      ...data,
                      backgroundImage: { ...data.backgroundImage!, parallax: e.target.checked },
                    })
                  }
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded-md focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Enable Parallax Effect</span>
              </label>
            </div>

            <OverlaySettings data={data} setFormData={setFormData} />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Background Color (fallback)
          </label>
          <input
            type="color"
            value={data.backgroundColor || '#ffffff'}
            onChange={e => setFormData({ ...data, backgroundColor: e.target.value })}
            className="w-full h-10 border border-gray-300 rounded-md"
          />
        </div>
      </div>
    </div>
  );
};
